import printJS from 'print-js-updated'

import {
  parseDocFooters,
  parseDocHeaders,
} from '#choo-app/lib/editor/plugins/configData'
import { getRevisionsState } from '#choo-app/lib/editor/plugins/revisions'
import { createSnapshot } from '#components/SnapshotLand/useSnapshotLandData'
import { IRoot } from '#state/types'
import { extractTsRestSuccess } from '#util/extractTsRest'
import { ScriptFormatConfiguration } from '#util/formats'
import {
  getRevisedPageNumbers,
  PageRevisionInfo,
} from '#util/pagination/getRevisedPageNumbers'
import { getMergedFormatDefinition } from '#util/prosemirrorHelpers'
import { RundownRowData, ScriptJson } from '#util/ScriptoApiClient/types'

// oxlint-disable-next-line no-unused-vars
import { saveBlobToFile, saveTextToFile } from '../fileDownload'
import { buildPrintableScriptHtml } from '../princePrinting'

import { filterPdfToPages } from './filterPdfPages'

export const getBlobAndSave = async ({
  downloadFn,
  fileName = 'Untitled',
}: {
  downloadFn: () => Promise<Blob | undefined>
  fileName?: string
}) => {
  const blob = await downloadFn()
  if (!blob) return
  saveBlobToFile({ blob, fileName })
}

export const getBlobAndPrint = async (
  downloadFn: () => Promise<Blob | undefined>,
) => {
  const blob = await downloadFn()
  if (!blob) return
  const href = URL.createObjectURL(blob)
  printJS({ printable: href, type: 'pdf' })
  URL.revokeObjectURL(href)
}

/**
 * Creates the pdf from a script: builds the html, then calls docraptor
 * to convert that html to pdf.
 */
export const getPdfBlobForScript = async ({
  scriptId,
  title,
  mst,
  rundownRowData,
}: {
  scriptId: string
  title: string
  mst: IRoot
  rundownRowData?: RundownRowData
}) => {
  const { apiClient, scrapi, user, view, currentOrg, currentScript } = mst
  const unwatermark = view.shouldUnwatermark
  const mstPrintPrefs = user.scriptPrintPrefs
  const revisionPrefs = user.getScriptRevisionPreferences(scriptId)
  const revisionPagesOnly = revisionPrefs.revisionRange !== 'entire'

  const editorState = currentScript?.pmEditor.editorState
  const revisionsPluginState = editorState
    ? getRevisionsState(editorState)
    : null
  const revisionTracker =
    revisionsPluginState?.tag === 'Resolved'
      ? revisionsPluginState.tracker
      : undefined

  const { id: snapshotId } = await createSnapshot(scrapi, {
    scriptId,
    autoSave: 'export',
  })

  const opts = { params: { scriptId, snapshotId } }
  const fn = mst.scrapi.scripts.getSnapshot(opts)
  const { body } = await extractTsRestSuccess(fn, 200)
  const { doc, scriptFormat } = body

  const mergedFormatDefinition = getMergedFormatDefinition(
    doc,
    scriptFormat.definition,
  )
  const formatDefinition = {
    ...mergedFormatDefinition,
    dialogueMoresAndContinueds:
      mergedFormatDefinition.moresContds?.enableDialogueContds,
  }

  const docHeaders = parseDocHeaders(doc.attrs?.headers)
  const docFooters = parseDocFooters(doc.attrs?.footers)

  // For rundown printing, preserve the rightCorner slot from user prefs —
  // it's a per-session rundown UI preference, not stored in script doc attrs.
  const headers =
    rundownRowData && mstPrintPrefs.headers?.slots?.rightCorner
      ? {
          ...docHeaders,
          slots: {
            ...docHeaders.slots,
            rightCorner: mstPrintPrefs.headers.slots.rightCorner,
          },
        }
      : docHeaders

  const basePrefs = { ...mstPrintPrefs, headers, footers: docFooters }
  const prefs = revisionPagesOnly
    ? { ...basePrefs, includeTitlePages: false }
    : basePrefs

  const html = buildPrintableScriptHtml({
    prefs,
    title,
    readRate: currentOrg?.readRate,
    rundownRowData,
    doc,
    formatDefinition,
    revisionTracker,
    revisionShowPageColors: revisionPrefs.revisionShowPageColors,
  })

  // DEV HACK: For debugging html generation
  // saveTextToFile({ text: html, fileName: 'test.html' })

  const pdfBlob = await apiClient.passThroughPrint({
    html,
    fileName: `${title}.pdf`,
    unwatermark,
  })

  if (revisionPagesOnly && revisionTracker && pdfBlob && editorState) {
    const revisedPageNumbers = getRevisedPageNumbers(
      editorState,
      revisionTracker,
    )

    const filterFn = (p: PageRevisionInfo) => {
      switch (revisionPrefs.revisionRange) {
        case 'revised':
          return p.hasRevision
        case 'set':
          return p.hasActiveRevision
        case 'entire':
        default:
          return false
      }
    }

    const filteredPageNumbers = revisedPageNumbers
      .filter(filterFn)
      .map((p) => p.index)

    if (filteredPageNumbers.length > 0) {
      return filterPdfToPages(pdfBlob, filteredPageNumbers)
    }
  }

  return pdfBlob
}

export const getPdfBlobForSection = async ({
  doc,
  title,
  mst,
  formatDefinition,
}: {
  doc: ScriptJson
  title: string
  mst: IRoot
  formatDefinition: ScriptFormatConfiguration
}) => {
  const { apiClient, user, view, currentOrg } = mst
  const unwatermark = view.shouldUnwatermark
  const mstPrefs = user.scriptPrintPrefs
  const sectionPrefs = {
    ...mstPrefs,
    headers: parseDocHeaders(doc.attrs?.headers),
    footers: parseDocFooters(doc.attrs?.footers),
  }

  const html = buildPrintableScriptHtml({
    prefs: sectionPrefs,
    title,
    readRate: currentOrg?.readRate,
    doc,
    formatDefinition,
  })

  return apiClient.passThroughPrint({
    html,
    fileName: `${title}.pdf`,
    unwatermark,
  })
}
